// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { WellFormedError } from '@/api/types';
import { AxiosError } from 'axios';
import { useMemo } from 'react';

/**
 * A memoized hook for handling API errors from React Query mutations.
 * Returns a well-formed error object when an error is present, or null otherwise.
 *
 * @param {AxiosError | null} error The error from a React Query mutation (e.g., mutation.error).
 * @return {WellFormedError | null} A memoized well-formed error object, or null if no error.
 *
 * @example
 * const mutation = useMutation({ mutationFn: ... });
 * const error = useHandleAPIError(mutation.error);
 * if (error?.apiStatusCode === ERROR_CODE_MAPPING.some_error) { ... }
 */
export function useHandleAPIError(
  error: AxiosError | null | undefined
): WellFormedError | null {
  return useMemo(() => (error ? handleAPIError(error) : null), [error]);
}
